/**
 * RestActions component
 *
 * Displays a set of grouped action cards related to data management,
 * student management, programming tools, and mass actions.
 *
 * Each card contains an icon, title, description, and one or more action buttons.
 * Some buttons perform navigations using React Router and custom navigation hooks.
 */

import React, { FC } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, DropdownButton, Select, Text } from "@foris/avocado-suite";
import SectionBox from "../section-box";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { Icon } from "@foris/avocado-icons";
import useGetModulesUrls from "@common/hooks/useGetModulesUrls";
import styles from "./restActions.module.scss";
import useContextUrl from "@common/hooks/useContextUrl";

const RestActions: FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { scenario, origin } = useContextUrl();
  const { adaptedDarwinUrl, catalogsUrl } = useNavigationUrl();

  const {
    catalogsOptions,
    reportsUrls,
    scenariosConfigurationUrls,
    productConfigurationUrls,
    userConfigurationUrls,
    forecastsUrls,
    assignationsUrls,
    massActionsUrls,
  } = useGetModulesUrls();

  return (
    <div className={styles.restActions}>
      {!!catalogsOptions?.length && (
        <SectionBox
          icon="database"
          title={t("home.action-cards.catalogs.title")}
          description={t("home.action-cards.catalogs.description")}
          titleExtraContent={
            <Button
              className={styles.btnRitgh}
              variant="ghost"
              rightIcon="chevron-right"
              size="sm"
              onClick={() => history.push(catalogsUrl())}
            >
              {t("home.action-cards.catalogs.btn-view-all")}
            </Button>
          }
          buttons={
            <div className={styles.catalogsSelect}>
              <Icon
                className={styles.catalogsSelect_icon}
                name="search"
                color="neutral-70"
                size="md"
              />

              <Select
                placeholder={t("home.action-cards.catalogs.search-catalogs")}
                options={catalogsOptions}
                escapeClearsValue
                value={null}
                formatOptionLabel={(option: any) => (
                  <div className={styles.catalogsSelect_option}>
                    <Text className={styles.optionLabel} weight="medium" type="sm">
                      {option?.self?.label}
                    </Text>

                    {!!option?.self?.category && (
                      <Text className={styles.optionCategory} weight="medium" type="xs">
                        {t("home.action-cards.catalogs.category")}: {option?.self?.category}
                      </Text>
                    )}
                  </div>
                )}
                onChange={(option: any) => {
                  window.open(
                    `${adaptedDarwinUrl}/${option?.self?.path}?scenario_id=${scenario}${
                      origin ? `&origin_id=${origin}` : ""
                    }`,
                    "_blank",
                  );
                }}
              />
            </div>
          }
        />
      )}

      {(!!forecastsUrls?.length || !!assignationsUrls?.length) && (
        <SectionBox
          icon="check-circle"
          title={t("home.action-cards.optimization-process.title")}
          description={t("home.action-cards.optimization-process.description")}
          buttons={
            <>
              {!!forecastsUrls?.length && (
                <DropdownButton variant="secondary" items={forecastsUrls}>
                  {t("home.action-cards.optimization-process.btn-forecasts")}
                </DropdownButton>
              )}

              {!!assignationsUrls?.length && (
                <DropdownButton variant="secondary" items={assignationsUrls}>
                  {t("home.action-cards.optimization-process.btn-assignations")}
                </DropdownButton>
              )}
            </>
          }
        />
      )}

      {!!reportsUrls?.length && (
        <SectionBox
          icon="report"
          title={t("home.action-cards.reports.title")}
          description={t("home.action-cards.reports.description")}
          buttons={
            <DropdownButton variant="secondary" items={reportsUrls}>
              {t("home.action-cards.reports.btn-reports")}
            </DropdownButton>
          }
        />
      )}

      {!!massActionsUrls?.length && (
        <SectionBox
          icon="folder-plus"
          title={t("home.action-cards.mass-actions.title")}
          description={t("home.action-cards.mass-actions.description")}
          buttons={
            <>
              {massActionsUrls?.map((option, key) => (
                <Button key={key} variant="secondary" onClick={option?.onClick}>
                  {option?.title}
                </Button>
              ))}
            </>
          }
        />
      )}

      {(!!productConfigurationUrls?.length || !!scenariosConfigurationUrls?.length) && (
        <SectionBox
          icon="settings"
          title={t("home.action-cards.configuration.title")}
          description={t("home.action-cards.configuration.description")}
          buttons={
            <>
              <DropdownButton variant="secondary" items={productConfigurationUrls}>
                {t("home.action-cards.configuration.btn-product-config")}
              </DropdownButton>

              <DropdownButton variant="secondary" items={scenariosConfigurationUrls}>
                {t("home.action-cards.configuration.btn-scenario-config")}
              </DropdownButton>
            </>
          }
        />
      )}

      {!!userConfigurationUrls?.length && (
        <SectionBox
          icon="user-check"
          title={t("home.action-cards.user.title")}
          description={t("home.action-cards.user.description")}
          buttons={
            <DropdownButton variant="secondary" items={userConfigurationUrls}>
              {t("home.action-cards.user.btn-config")}
            </DropdownButton>
          }
        />
      )}
    </div>
  );
};

export default RestActions;
